import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useAuth } from 'contexts/AuthContext';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthService from 'services/AuthService';

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname ?? '/';

    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    const { control, handleSubmit, formState: { errors } } = useForm({
        defaultValues: { username: '', password: '' },
    });

    const onSubmit = async ({ username, password }) => {
        setServerError('');
        setIsLoading(true);
        try {
            const response = await AuthService.login(username, password);
            if (response?.data) {
                login(response.data);
                navigate(from, { replace: true });
            } else {
                setServerError(response?.error?.message ?? 'Login failed. Please try again.');
            }
        } catch (error) {
            setServerError(
                error?.data?.error?.message ||
                error?.message ||
                'Login failed. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                {/* Brand */}
                <div style={styles.brand}>
                    <div style={styles.logoMark}>T</div>
                    <span style={styles.brandName}>Trident Algo</span>
                </div>

                <h1 style={styles.title}>Welcome back</h1>
                <p style={styles.subtitle}>Sign in to your account to continue</p>

                {serverError && (
                    <div style={styles.errorBanner}>
                        <span style={styles.errorIcon}>⚠</span>
                        {serverError}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
                    <div style={styles.field}>
                        <label style={styles.label}>Username</label>
                        <Controller
                            name="username"
                            control={control}
                            rules={{ required: 'Username is required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    placeholder="Enter your username"
                                    error={!!errors.username}
                                    inputProps={{ autoComplete: 'username' }}
                                />
                            )}
                        />
                        {errors.username && (
                            <span style={styles.fieldError}>{errors.username.message}</span>
                        )}
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Password</label>
                        <Controller
                            name="password"
                            control={control}
                            rules={{ required: 'Password is required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    error={!!errors.password}
                                    inputProps={{ autoComplete: 'current-password' }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword((v) => !v)}
                                                    edge="end"
                                                    size="small"
                                                    tabIndex={-1}
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                        {errors.password && (
                            <span style={styles.fieldError}>{errors.password.message}</span>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                            ...styles.submitBtn,
                            ...(isLoading ? styles.submitBtnDisabled : {}),
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={18} style={{ color: '#030712' }} />
                        ) : (
                            'Sign In'
                        )}
                    </button>
                </form>

                <p style={styles.footerText}>
                    Don&apos;t have an account?{' '}
                    <Link to="/signup" style={styles.link}>
                        Create one
                    </Link>
                </p>
            </div>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        backgroundColor: '#06090b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        fontFamily: 'Inter, sans-serif',
    },
    card: {
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#121212',
        border: '1px solid #ffffff0c',
        borderRadius: '16px',
        padding: '2.5rem 2rem',
    },
    brand: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        marginBottom: '2rem',
    },
    logoMark: {
        width: '36px',
        height: '36px',
        backgroundColor: '#facc15',
        color: '#030712',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '800',
        fontSize: '1.1rem',
        flexShrink: 0,
    },
    brandName: {
        color: '#f3f4f6',
        fontSize: '1.1rem',
        fontWeight: '700',
        letterSpacing: '-0.02em',
    },
    title: {
        color: '#f9fafb',
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: '0 0 0.375rem 0',
        letterSpacing: '-0.02em',
    },
    subtitle: {
        color: '#6b7280',
        fontSize: '0.875rem',
        margin: '0 0 1.75rem 0',
    },
    errorBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: '#1f1010',
        border: '1px solid #7f1d1d',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        color: '#fca5a5',
        fontSize: '0.875rem',
        marginBottom: '1.25rem',
    },
    errorIcon: {
        fontSize: '1rem',
        flexShrink: 0,
    },
    field: {
        marginBottom: '1.25rem',
    },
    label: {
        display: 'block',
        color: '#d1d5db',
        fontSize: '0.875rem',
        fontWeight: '500',
        marginBottom: '0.4rem',
    },
    fieldError: {
        display: 'block',
        color: '#f87171',
        fontSize: '0.78rem',
        marginTop: '0.3rem',
    },
    submitBtn: {
        width: '100%',
        padding: '0.65rem',
        backgroundColor: '#facc15',
        color: '#030712',
        border: 'none',
        borderRadius: '8px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.9375rem',
        fontWeight: '600',
        cursor: 'pointer',
        marginTop: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'background-color 0.15s ease',
        minHeight: '42px',
    },
    submitBtnDisabled: {
        backgroundColor: '#a37f00',
        cursor: 'not-allowed',
    },
    footerText: {
        color: '#6b7280',
        fontSize: '0.875rem',
        textAlign: 'center',
        marginTop: '1.5rem',
        marginBottom: 0,
    },
    link: {
        color: '#facc15',
        textDecoration: 'none',
        fontWeight: '500',
    },
};

export default Login;
